import * as React from 'react';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Sidebar, type NavPermissions } from './sidebar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useAuthStore } from '@/store/auth-store';
import { useUiStore } from '@/store/ui-store';
import { useThemeStore } from '@/store/theme-store';
import { cn } from '@/lib/utils';

interface AppShellProps {
  children: React.ReactNode;
  nav: NavPermissions;
}

/** 路由前缀 → 页面名,按前缀长度降序排列,确保最长前缀优先匹配(详情页归到其列表名)。 */
const PAGE_TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/ads/ad-account-transactions', '广告账户流水'],
  ['/ads/spend-details', '消耗明细'],
  ['/ads/ad-platforms', '广告平台管理'],
  ['/ads/ad-providers', '广告户商管理'],
  ['/ads/materials/display', '投放素材'],
  ['/ads/materials/review', '审核素材'],
  ['/ads/material-process', '素材加工'],
  ['/ads/audit-logs', '修改记录'],
  ['/ads/dashboard', '仪表盘'],
  ['/ads/operators', '投手管理'],
  ['/ads/accounts', '广告账户管理'],
  ['/ads/users', '用户管理'],
  ['/ads/apps', 'App 管理'],
];

function resolvePageTitle(pathname: string): string {
  const match = PAGE_TITLES.find(([prefix]) => pathname.startsWith(prefix));
  return match?.[1] ?? '投放管理后台';
}

export function AppShell({ children, nav }: AppShellProps) {
  const username = useAuthStore((s) => s.username);
  const clearSession = useAuthStore((s) => s.clear);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pageTitle = resolvePageTitle(pathname);
  const initials = (username || '管理员').slice(0, 2).toUpperCase();

  const handleLogout = () => {
    clearSession();
    void navigate({ to: '/login' });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar nav={nav} />
      <div
        className={cn(
          'flex flex-1 flex-col overflow-hidden transition-[margin] duration-200',
          collapsed ? 'ml-[4.5rem]' : 'ml-60',
        )}
      >
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between border-b border-border bg-card/70 px-6 backdrop-blur-md">
          <div className="flex items-center gap-2.5">
            <div className="h-4 w-1 rounded-full bg-primary" />
            <div className="text-sm font-semibold tracking-tight text-foreground">{pageTitle}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? '切换到明亮模式' : '切换到深色模式'}
              title={theme === 'dark' ? '切换到明亮模式' : '切换到深色模式'}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-full outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                theme === 'dark' ? 'text-warning' : 'text-primary',
              )}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary outline-none transition-colors hover:bg-primary/25 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="用户菜单"
                >
                  {initials}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-52 p-1.5">
                <div className="px-2 py-1.5">
                  <div className="truncate text-sm font-medium text-foreground">
                    {username || '管理员'}
                  </div>
                  <div className="text-xs text-muted-foreground">当前登录</div>
                </div>
                <Separator className="my-1" />
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive transition-colors hover:bg-destructive/10"
                >
                  <LogOut size={15} />
                  退出登录
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </header>
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-background px-8 py-6">
          <div className="flex h-full min-h-0 w-full flex-col">{children}</div>
        </main>
      </div>
    </div>
  );
}
